import { createTheme, ThemeOptions, PaletteMode } from '@mui/material/styles';

// FamilyPal color palette matching mobile app
export const colors = {
  primary: 'rgb(2, 95, 154)',
  primaryLight: '#96D0F0',
  background: '#F7F7F7',
  white: '#FFFFFF',
  gray: '#777777',
  lightGray: '#AAAAAA',
  border: '#d5d5d5',
  shadow: '#CCCCCC',
  error: '#FF0000',

  // Feature card colors
  calendar: '#96D0F0',
  messages: '#AC8EB8',
  notes: '#DC5D72',
  money: '#EDA745',
  memories: '#A0BC60',
  contacts: '#96D0F0',
  activities: '#AC8EB8',
  health: '#DC5D72',
  school: '#EDA745',
  discipline: '#A0BC60',
};

// Member color options
export const memberColors = [
  '#96D0F0',
  '#AC8EB8',
  '#DC5D72',
  '#EDA745',
  '#A0BC60',
  '#5D9CEC',
  '#48CFAD',
  '#EC87C0',
  '#FFCE54',
  '#A0D468',
];

// Role options for family members
export const roles = [
  { key: 'mom', label: 'Mom' },
  { key: 'dad', label: 'Dad' },
  { key: 'child', label: 'Child' },
  { key: 'stepparent', label: 'Step-Parent' },
  { key: 'grandparent', label: 'Grandparent' },
  { key: 'caregiver', label: 'Caregiver' },
  { key: 'other', label: 'Other' },
];

// Spacing scale
export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
};

const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
    },
    secondary: {
      main: colors.messages,
    },
    error: {
      main: colors.error,
    },
    background: {
      default: mode === 'light' ? colors.background : '#121212',
      paper: mode === 'light' ? colors.white : '#1e1e1e',
    },
    text: {
      primary: mode === 'light' ? '#333333' : '#ffffff',
      secondary: mode === 'light' ? colors.gray : '#b0b0b0',
    },
    divider: mode === 'light' ? colors.border : '#333333',
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
    caption: {
      fontSize: '0.75rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(2, 95, 154, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(2, 95, 154, 0.15)',
            },
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 500,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: '0.75rem',
        },
      },
    },
    // DataGrid styles are applied via sx prop to avoid type issues
  },
});

export const createAppTheme = (mode: PaletteMode) => createTheme(getDesignTokens(mode));

// Default light theme
export const lightTheme = createAppTheme('light');

// Dark theme
export const darkTheme = createAppTheme('dark');
